import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { graphql } from "@/gen";

const seriesPlayersQuery = graphql(`
    query SeriesPlayers($seriesId: UUID!) {
        seriesPlayers(seriesId: $seriesId) {
            id
            name
        }
    }
`);

const seriesStandingsQuery = graphql(`
    query SeriesStandings($seriesId: UUID!) {
        seriesStandings(seriesId: $seriesId) {
            playerId
            points
        }
    }
`);

const updateStandingMutation = graphql(`
    mutation UpdateTournamentSeriesStanding($input: UpdateTournamentSeriesStandingInput!) {
        updateTournamentSeriesStanding(input: $input) {
            tournamentSeriesId
            playerId
            points
            createdAt
        }
    }
`);

export function TournamentSeriesStandings({ seriesId }: { seriesId: string }) {
    const [playersResult] = useQuery({
        query: seriesPlayersQuery,
        variables: { seriesId },
        pause: !seriesId,
    });

    const [standingsResult] = useQuery({
        query: seriesStandingsQuery,
        variables: { seriesId },
        pause: !seriesId,
    });

    const [_, updateStanding] = useMutation(updateStandingMutation);

    const players = useMemo(
        () => playersResult.data?.seriesPlayers || [],
        [playersResult.data?.seriesPlayers]
    );
    const standingsData = standingsResult.data?.seriesStandings || [];

    const initialStandingsMap = players.reduce(
        (acc, player) => {
            const found = standingsData.find(s => s.playerId === player.id);
            acc[player.id] = found ? found.points : 0;
            return acc;
        },
        {} as { [key: string]: number }
    );

    const [editedStandings, setEditedStandings] = useState<{ [key: string]: number }>(
        initialStandingsMap
    );

    useEffect(() => {
        const currentKeys = Object.keys(editedStandings).sort().join(",");
        const newKeys = players
            .map(p => p.id)
            .sort()
            .join(",");

        if (currentKeys !== newKeys) {
            setEditedStandings(initialStandingsMap);
        }
    }, [editedStandings, initialStandingsMap, players]);

    const playerStandings = players.map(player => ({
        id: player.id,
        name: player.name,
        points: editedStandings[player.id] ?? 0,
    }));
    playerStandings.sort((a, b) => b.points - a.points);

    const handleUpdateStandings = async () => {
        for (const player of playerStandings) {
            const points = editedStandings[player.id];
            const res = await updateStanding({
                input: { tournamentSeriesId: seriesId, playerId: player.id, points },
            });
            if (res.error) {
                toast.error(`Error updating points for ${player.name}: ${res.error.message}`);
                return;
            }
        }
        toast.success("Standings updated");
    };

    return (
        <div className="my-4 w-full">
            <h3 className="mb-4 text-lg font-bold">Standings</h3>
            <Table className="w-full table-auto">
                <TableHeader>
                    <TableRow>
                        <TableCell className="w-10">#</TableCell>
                        <TableCell>Player</TableCell>
                        <TableCell>Points</TableCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {playerStandings.map((player, index) => (
                        <TableRow key={player.id}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{player.name}</TableCell>
                            <TableCell>
                                <Input
                                    type="number"
                                    value={player.points}
                                    onChange={e =>
                                        setEditedStandings(prev => ({
                                            ...prev,
                                            [player.id]: parseFloat(e.target.value),
                                        }))
                                    }
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={handleUpdateStandings}>
                    Update standings
                </Button>
            </div>
        </div>
    );
}
